import { useHistory, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { IContract } from "./ListContracts.types";
import { empresaDaTela } from "./empresa-da-tela";
import { Addressable } from "../Addressable/Addressable";
import { Governance } from "../Governance/Governance";
import { CargaOperadora } from "../CargaOperadora/CargaOperadora";
import { RaioX } from "../RaioX";
import { CarteirinhasTab } from "v2/pages/Collaborators/CarteirinhasTab/CarteirinhasTab";
import { DocumentosTab } from "v2/pages/Collaborators/DocumentosTab/DocumentosTab";

import api from "services/api";
import { useAuth } from "contexts/auth";

import { Button, FormControl, InputLabel, MenuItem, Select, Typography, Box, Card, CardContent, Grid, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from "@mui/material";
import { ArrowForward, ArrowBack } from "@mui/icons-material";

type ViewMode = 'selection' | 'contracts' | 'activities';

export function ListContracts() {
  const { organizationId } = useParams() as { organizationId: string };
  const history = useHistory();
  const { user } = useAuth();
  // A URL traz a outra parte do contrato; as atividades pedem a empresa (empresa-da-tela.ts).
  const empresaId = empresaDaTela(user, organizationId);

  const [viewMode, setViewMode] = useState<ViewMode>('selection');
  const [showAddressable, setShowAddressable] = useState(false);
  const [showCarteirinhas, setShowCarteirinhas] = useState(false);
  const [showDocumentos, setShowDocumentos] = useState(false);
  const [showGovernance, setShowGovernance] = useState(false);
  const [showCargaOperadora, setShowCargaOperadora] = useState(false);
  const [showRaioX, setShowRaioX] = useState(false);

  // Estado para modo de agrupamento (por produto ou por fornecedor)
  const [groupBy, setGroupBy] = useState<'product' | 'supplier'>('product');

  // Estado para filtros
  const [filters, setFilters] = useState({
    status: "TODOS",
    supplier: "TODOS",
    product: "TODOS",
  });

  const { data, isFetching, isFetched, isError } = useQuery({
    queryKey: ["contracts", organizationId],
    queryFn: () => api.get<{ data: IContract[] }>(`/v2/organizations/${organizationId}/workspaces`),
    select: (data) => data.data.data,
    // queryFn: () => api.get(`/v2/workspaces?sellerId=${sellerId}&buyerId=${buyerId}`),
    // select: (data) => data.data.data as IContract[],
  });

  // Função para aplicar filtros
  function applyFilters(contracts: IContract[]): IContract[] {
    return contracts.filter((contract) => {
      const matchesSupplier = filters.supplier === "TODOS" ||
        contract?.product?.supplierOrganization?.corporateName === filters.supplier;

      const matchesProduct = filters.product === "TODOS" ||
        contract?.product?.name === filters.product;

      // Status sempre "Ativo" conforme solicitado
      const matchesStatus = filters.status === "TODOS" || filters.status === "ATIVO";

      return matchesSupplier && matchesProduct && matchesStatus;
    });
  }

  // Função para agrupar por produto
  function groupByProduct(contracts: IContract[]): [string, IContract[]][] {
    const grouped = contracts.reduce((acc, contract) => {
      const productName = contract?.product?.name || "Sem Produto";
      if (!acc[productName]) acc[productName] = [];
      acc[productName].push(contract);
      return acc;
    }, {} as Record<string, IContract[]>);

    return Object.entries(grouped);
  }

  // Função para agrupar por fornecedor
  function groupBySupplier(contracts: IContract[]): [string, IContract[]][] {
    const grouped = contracts.reduce((acc, contract) => {
      const supplierName = contract?.product?.supplierOrganization?.corporateName || "Sem Fornecedor";
      if (!acc[supplierName]) acc[supplierName] = [];
      acc[supplierName].push(contract);
      return acc;
    }, {} as Record<string, IContract[]>);

    return Object.entries(grouped);
  }

  // Extrair valores únicos para os filtros
  const uniqueSuppliers = data ? Array.from(new Set(
    data.map(c => c?.product?.supplierOrganization?.corporateName).filter(Boolean)
  )) : [];

  const uniqueProducts = data ? Array.from(new Set(
    data.map(c => c?.product?.name).filter(Boolean)
  )) : [];

  // Selection View Component
  const renderSelectionView = () => (
    <Box sx={{ width: '100%', maxWidth: '1200px', margin: '0 auto' }}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              height: '180px',
              border: '1px solid rgba(0, 0, 0, 0.09)',
              borderRadius: '8px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              padding: '16px'
            }}
          >
            <CardContent sx={{ padding: 0, '&:last-child': { paddingBottom: 0 } }}>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 'bold',
                  color: 'primary.main',
                  marginBottom: '4px'
                }}
              >
                Contratos
              </Typography>
              <Typography
                variant="subtitle2"
                sx={{
                  color: 'text.secondary',
                  fontSize: '14px',
                  lineHeight: '1.57'
                }}
              >
                Visualize os contratos ativos e acesse facilmente as atividades relacionadas a cada um deles. Realize movimentações, e aplique exceções conforme necessário.
              </Typography>
            </CardContent>
            <Button
              variant="outlined"
              color="primary"
              fullWidth
              endIcon={<ArrowForward />}
              onClick={() => setViewMode('contracts')}
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              Acessar
            </Button>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card
            sx={{
              height: '180px',
              border: '1px solid rgba(0, 0, 0, 0.09)',
              borderRadius: '8px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              padding: '16px'
            }}
          >
            <CardContent sx={{ padding: 0, '&:last-child': { paddingBottom: 0 } }}>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 'bold',
                  color: 'primary.main',
                  marginBottom: '4px'
                }}
              >
                Atividades de CNPJ
              </Typography>
              <Typography
                variant="subtitle2"
                sx={{
                  color: 'text.secondary',
                  fontSize: '14px',
                  lineHeight: '1.57'
                }}
              >
                Nas atividades, o usuário pode visualizar a sincronização com o eSocial, gerenciar a governança de dados e escolher o setup de endereçabilidade configurado pelo corretor.
              </Typography>
            </CardContent>
            <Button
              variant="outlined"
              color="primary"
              fullWidth
              endIcon={<ArrowForward />}
              onClick={() => setViewMode('activities')}
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              Acessar
            </Button>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  // Contracts List View (nova interface)
  const renderContractsView = () => {
    if (!data) return null;

    const filteredData = applyFilters(data);
    const groupedData = groupBy === 'product'
      ? groupByProduct(filteredData)
      : groupBySupplier(filteredData);

    return (
      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {/* Título */}
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Contratos
        </Typography>

        {/* Seção de Visualização */}
        <Box sx={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
          <Typography variant="body2" sx={{ fontSize: '13px' }}>
            Visualização
          </Typography>
          <Chip
            label="Por Produto"
            color={groupBy === 'product' ? 'primary' : 'default'}
            onClick={() => setGroupBy('product')}
            size="medium"
            sx={{
              fontWeight: 'bold',
              cursor: 'pointer',
              '&.MuiChip-colorDefault': {
                backgroundColor: 'rgba(25, 28, 26, 0.08)'
              }
            }}
          />
          <Chip
            label="Por Fornecedor"
            color={groupBy === 'supplier' ? 'primary' : 'default'}
            onClick={() => setGroupBy('supplier')}
            size="medium"
            sx={{
              fontWeight: 'bold',
              cursor: 'pointer',
              '&.MuiChip-colorDefault': {
                backgroundColor: 'rgba(25, 28, 26, 0.08)'
              }
            }}
          />
        </Box>

        {/* Seção de Filtros */}
        <Box sx={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant="body2" sx={{ fontSize: '13px' }}>
            Filtros
          </Typography>

          <FormControl size="small" sx={{ width: '156px' }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.status}
              label="Status"
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            >
              <MenuItem value="TODOS">Todos</MenuItem>
              <MenuItem value="ATIVO">Ativo</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ width: '156px' }}>
            <InputLabel>Fornecedor</InputLabel>
            <Select
              value={filters.supplier}
              label="Fornecedor"
              onChange={(e) => setFilters({ ...filters, supplier: e.target.value })}
            >
              <MenuItem value="TODOS">Todos</MenuItem>
              {uniqueSuppliers.map((supplier) => (
                <MenuItem key={supplier} value={supplier}>
                  {supplier}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ width: '156px' }}>
            <InputLabel>Produto</InputLabel>
            <Select
              value={filters.product}
              label="Produto"
              onChange={(e) => setFilters({ ...filters, product: e.target.value })}
            >
              <MenuItem value="TODOS">Todos</MenuItem>
              {uniqueProducts.map((product) => (
                <MenuItem key={product} value={product}>
                  {product}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Cards de Contratos Agrupados */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {groupedData.map(([groupName, contracts]) => (
            <Card
              key={groupName}
              sx={{
                border: '1px solid rgba(0, 0, 0, 0.06)',
                borderRadius: '16px',
                padding: '16px',
                boxShadow: 'none'
              }}
            >
              {/* Logo Placeholder - apenas para visualização por fornecedor */}
              {groupBy === 'supplier' && (
                <Box
                  component="img"
                  src={`/assets/logos_operadoras/${groupName}.png`}
                  alt={groupName}
                  sx={{
                    width: '86px',
                    height: '30px',
                    objectFit: 'contain',
                    borderRadius: '4px',
                    marginBottom: '16px'
                  }}
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              )}

              {/* Header do Grupo (quando agrupado por produto) */}
              {groupBy === 'product' && (
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    marginBottom: '16px',
                    fontSize: '20px'
                  }}
                >
                  {groupName}
                </Typography>
              )}

              {/* Lista de Contratos */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {contracts.map((contract) => (
                  <Box
                    key={contract.id}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: '16px',
                      padding: '8px 16px',
                      borderRadius: '16px',
                      backgroundColor: 'transparent',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s',
                      '&:hover': {
                        backgroundColor: 'rgba(48, 36, 225, 0.08)'
                      }
                    }}
                    onClick={() => history.push(`/v2/workspaces/${contract.id}`)}
                  >
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 'bold',
                        fontSize: '16px',
                        flex: 1
                      }}
                    >
                      {`${contract.policyContractNumber || contract.id.split('-')[0].toUpperCase()} - ${contract.product.supplierOrganization.corporateName} - ${contract.product.name}`}
                                          {/* Logo na row quando agrupado por produto */}
                    {groupBy === 'product' && (
                      <Box
                        component="img"
                        src={`/assets/logos_operadoras/${contract.supplierCorporateName}.png`}
                        alt={contract.supplierCorporateName}
                        sx={{
                          width: '86px',
                          height: '30px',
                          objectFit: 'contain',
                          borderRadius: '4px',
                          flexShrink: 0
                        }}
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    )}
                    </Typography>

                    <Chip
                      label="Ativo"
                      color="success"
                      size="small"
                      sx={{ fontWeight: 'bold' }}
                    />
                  </Box>
                ))}
              </Box>
            </Card>
          ))}
        </Box>

        {/* Mensagem quando não há dados */}
        {groupedData.length === 0 && (
          <Box sx={{ textAlign: 'center', padding: '40px' }}>
            <Typography variant="body1" color="text.secondary">
              Nenhum contrato encontrado com os filtros selecionados.
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  // Activities View
  const renderActivitiesView = () => {
    if (showCarteirinhas) {
      return (
        <Box sx={{ width: '100%' }}>
          <Box sx={{ marginBottom: '16px' }}>
            <Button
              startIcon={<ArrowBack />}
              onClick={() => setShowCarteirinhas(false)}
              sx={{ textTransform: 'none' }}
            >
              Voltar para Atividades
            </Button>
          </Box>
          <CarteirinhasTab organizationId={empresaId} />
        </Box>
      );
    }

    if (showDocumentos) {
      return (
        <Box sx={{ width: '100%' }}>
          <Box sx={{ marginBottom: '16px' }}>
            <Button
              startIcon={<ArrowBack />}
              onClick={() => setShowDocumentos(false)}
              sx={{ textTransform: 'none' }}
            >
              Voltar para Atividades
            </Button>
          </Box>
          <DocumentosTab organizationId={empresaId} />
        </Box>
      );
    }

    // Se Addressable deve ser mostrado, renderizar apenas ele
    if (showAddressable) {
      return (
        <Box sx={{ width: '100%' }}>
          <Box sx={{ marginBottom: '16px' }}>
            <Button
              startIcon={<ArrowBack />}
              onClick={() => setShowAddressable(false)}
              sx={{ textTransform: 'none' }}
            >
              Voltar para Atividades
            </Button>
          </Box>
          <Addressable organizationId={empresaId} />
        </Box>
      );
    }

    // Se Governance deve ser mostrado, renderizar apenas ele
    if (showGovernance) {
      return (
        <Box sx={{ width: '100%' }}>
          {/* O cabeçalho vive dentro do Governance: ele troca para "Voltar para o histórico"
              quando um upload específico está aberto. */}
          <Governance organizationId={empresaId} onBack={() => setShowGovernance(false)} />
        </Box>
      );
    }

    if (showCargaOperadora) {
      return (
        <Box sx={{ width: '100%' }}>
          {/* O cabeçalho vive dentro da CargaOperadora, junto com a própria seta de voltar. */}
          <CargaOperadora organizationId={empresaId} onBack={() => setShowCargaOperadora(false)} />
        </Box>
      );
    }

    if (showRaioX) {
      return (
        <Box sx={{ width: '100%' }}>
          {/* why: o cabeçalho e o botão de voltar vivem dentro do RaioX, no mesmo padrão de Governance e CargaOperadora. */}
          <RaioX organizationId={organizationId} onBack={() => setShowRaioX(false)} />
        </Box>
      );
    }

    // Atividades hardcoded
    const activities = [
      { id: 1, name: 'Sincronização com eSocial', status: 'Executado', clickable: false },
      { id: 2, name: 'Gestão de endereçabilidade', status: 'Executado', clickable: true },
      { id: 3, name: 'Carteirinhas', status: 'Disponível', clickable: true },
      { id: 4, name: 'Carregar Beneficiários', status: 'Disponível', clickable: true },
      { id: 5, name: 'Carga operadora', status: 'Disponível', clickable: true },
      { id: 7, name: 'Documentos', status: 'Disponível', clickable: true },
    ];
    // O backend do Raio-X só atende corretora (authorizeSeller) e recusa a empresa.
    if (user?.isSeller) activities.push({ id: 6, name: 'Raio-X de Configuração', status: 'Disponível', clickable: true });

    return (
      <Box sx={{ width: '100%' }}>
        <Typography variant="h6" sx={{ fontWeight: 600, marginBottom: '16px' }}>
          Atividades
        </Typography>

        <TableContainer sx={{ "& .MuiTableCell-root": { background: "#FFFFFF !important" } }}>
          <Table sx={{ backgroundColor: 'white' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '14px' }}>Nome</TableCell>
                <TableCell sx={{ fontWeight: 'bold', fontSize: '14px', width: '150px' }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {activities.map((activity) => (
                <TableRow
                  key={activity.id}
                  sx={{
                    cursor: activity.clickable ? 'pointer' : 'default',
                    '&:hover': activity.clickable ? {
                      backgroundColor: 'rgba(48, 36, 225, 0.08)'
                    } : {},
                  }}
                  onClick={() => {
                    if (!activity.clickable) return;
                    // Explicit per-activity routing so a future clickable activity
                    // doesn't fall through to Endereçabilidade by default.
                    if (activity.id === 2) setShowAddressable(true);
                    else if (activity.id === 3) setShowCarteirinhas(true);
                    else if (activity.id === 4) setShowGovernance(true);
                    else if (activity.id === 5) setShowCargaOperadora(true);
                    else if (activity.id === 6) setShowRaioX(true);
                    else if (activity.id === 7) setShowDocumentos(true);
                  }}
                >
                  <TableCell>{activity.name}</TableCell>
                  <TableCell>
                    <Chip
                      label={activity.status}
                      color="success"
                      size="small"
                      sx={{ fontWeight: 'bold' }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  return (
    <>
      {!isFetched && isFetching && <div>Loading...</div>}
      {!isFetched && isError && <div>Error...</div>}

      {data && (
        <>
          {/* Back to selection — shown on the contracts view and on the activities
              list (but not inside an activity sub-view, which has its own back). */}
          {(viewMode === 'contracts' ||
            (viewMode === 'activities' && !showAddressable && !showCarteirinhas && !showDocumentos && !showGovernance && !showCargaOperadora && !showRaioX)) && (
            <Box sx={{ marginBottom: '16px' }}>
              <Button
                startIcon={<ArrowBack />}
                onClick={() => {
                  setViewMode('selection');
                  setShowAddressable(false);
                  setShowCarteirinhas(false);
                  setShowDocumentos(false);
                  setShowGovernance(false);
                  setShowCargaOperadora(false);
                  setShowRaioX(false);
                }}
                sx={{ textTransform: 'none' }}
              >
                Voltar
              </Button>
            </Box>
          )}

          {/* Conditional rendering based on viewMode */}
          {viewMode === 'selection' && renderSelectionView()}
          {viewMode === 'contracts' && renderContractsView()}
          {viewMode === 'activities' && renderActivitiesView()}
        </>
      )}
    </>
  );
}
